import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Button, Divider, Grid } from '@mui/material';
import { LinkedIn } from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import carlosPhoto from '../assets/img/author-carlos.jpg';
import nataliaPhoto from '../assets/img/author-natalia.jpg';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FinMaple',
  url: 'https://www.finmaple.com',
  logo: 'https://www.finmaple.com/logo.png',
  description: 'Plataforma de educación financiera para latinos en Canadá',
  founder: {
    '@type': 'Person',
    name: 'Carlos Alviz',
    jobTitle: 'Fundador',
  },
  sameAs: [
    'https://www.linkedin.com/in/alvizcarlos/',
  ],
};

const SobreNosotrosPage = () => {
  const { t, i18n } = useTranslation();

  const teamMembers = [
    {
      name: 'Carlos Alviz',
      role: t('pages.about.team.carlos.role'),
      photo: carlosPhoto,
      linkedin: 'https://www.linkedin.com/in/alvizcarlos/',
      bio: t('pages.about.team.carlos.bio'),
    },
    {
      name: 'Natalia Duarte',
      role: t('pages.about.team.natalia.role'),
      photo: nataliaPhoto,
      linkedin: 'https://www.linkedin.com/in/natalia-duarteh/',
      bio: t('pages.about.team.natalia.bio'),
    },
  ];

  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="about"
        title={t('pages.about.seoTitle')}
        description={t('pages.about.seoDescription')}
        canonical="/sobre-nosotros"
        routeKey="about"
        schema={schema}
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        variant="functional"
        title={t('pages.about.title')}
        subtitle={t('pages.about.subtitle')}
        breadcrumbs={[{ label: t('pages.about.breadcrumb') }]}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>

        {/* Why we created FinMaple */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.about.whyTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.about.whyParagraph1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.about.whyParagraph2')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8 }}>
            {t('pages.about.whyParagraph3')}
          </Typography>
        </Box>

        {/* Our Team */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 4 }}>
            {t('pages.about.teamTitle')}
          </Typography>
          <Grid container spacing={4}>
            {teamMembers.map((member) => (
              <Grid item xs={12} sm={6} key={member.name}>
                <Box sx={{ textAlign: 'center' }}>
                  {member.photo ? (
                    <Box
                      component="img"
                      src={member.photo}
                      alt={member.name}
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        mx: 'auto',
                        mb: 2,
                        border: '3px solid #1E3A5F',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        bgcolor: '#1E3A5F',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: '2rem',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {member.name.split(' ').map((n) => n[0]).join('')}
                    </Box>
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                    {member.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1E3A5F', fontWeight: 600, mb: 1 }}>
                    {member.role}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 1.5 }}>
                    {member.bio}
                  </Typography>
                  <Button
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LinkedIn />}
                    size="small"
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      color: '#1E3A5F',
                      borderColor: '#1E3A5F',
                      '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' },
                    }}
                  >
                    LinkedIn
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Our Mission */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.about.missionTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.about.missionParagraph1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8 }}>
            {t('pages.about.missionParagraph2')}
          </Typography>
        </Box>

        {/* Affiliate Disclosure */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.about.affiliateTitle', { defaultValue: 'Cómo nos financiamos' })}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.about.affiliateParagraph1', { defaultValue: 'FinMaple es gratuito para todos los usuarios. Nos financiamos a través de comisiones de referido (affiliate links) cuando abres una cuenta o solicitas un producto financiero desde nuestros enlaces. Esto no tiene costo adicional para ti.' })}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8 }}>
            {t('pages.about.affiliateParagraph2', { defaultValue: 'Nuestras calificaciones (FinMaple Score) son independientes y nunca están influenciadas por compensación. Puedes leer más sobre nuestra metodología en nuestra página de transparencia.' })}
          </Typography>
        </Box>

        {/* Financial Disclaimer */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, fontStyle: 'italic' }}>
            {t('pages.about.disclaimer')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SobreNosotrosPage;
